import { CommonModule } from '@angular/common';
import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { WelcomeImageComponent } from '../../../components/welcome-image/welcome-image.component';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { finalize } from 'rxjs/operators';
import { LoginHandlerFactory } from '../../../../core/handlers/factories/login-handler-factory';
import { LoginContext } from '../../../../core/models/auth/login.context';
import { OrganizationService } from 'app/core/services/organization/organization.service';
import { CurrentUserService } from 'app/core/services/auth/current-user.service';

declare var KTMenu: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    WelcomeImageComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, AfterViewInit {
  loginForm: FormGroup;
  firstLoginForm: FormGroup;
  mailCodeForm: FormGroup;

  showFirstLogin: boolean = false;
  show2FA: boolean = false;
  showMailSection: boolean = false;
  showLoginWithMail: boolean = false;
  showLoginWithGoogle: boolean = false;
  googleCodeForm: FormGroup;
  showGoogleSection: boolean = false;
  loading: boolean = false;
  loginPasswordVisible: boolean = false;

  tempEmail: string = '';
  tempPassword: string = '';

  languages = [
    {
      code: 'en-US',
      name: 'English',
      flag: 'assets/media/flags/united-states.png',
    },
    {
      code: 'nl-NL',
      name: 'Dutch',
      flag: 'assets/media/flags/netherlands.png',
    },
    { code: 'tr-TR', name: 'Turkish', flag: 'assets/media/flags/turkey.png' },
  ];

  currentLanguageName: string = '';
  currentLanguageFlag: string = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private loginHandlerFactory: LoginHandlerFactory,
    private orgService: OrganizationService,
    private currentUserService: CurrentUserService,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
    const selectedLang =
      this.languages.find((l) => l.code === userLang) || this.languages[0];
    this.updateCurrentLanguage(selectedLang);

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [false],
    });

    this.firstLoginForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    });

    this.mailCodeForm = this.fb.group({
      code: ['', Validators.required],
    });

    this.googleCodeForm = this.fb.group({
      code: ['', Validators.required],
    });
  }

  ngOnInit() {
    const savedEmail = localStorage.getItem('savedEmail');
    if (savedEmail) {
      this.loginForm.patchValue({
        email: savedEmail,
        rememberMe: true,
      });
    }
  }

  ngAfterViewInit(): void {
    if (typeof KTMenu !== 'undefined') {
      KTMenu.createInstances();
    }
  }

  setLanguage(lang: any) {
    this.translate.use(lang.code);
    localStorage.setItem('app-lang', lang.code);
    this.updateCurrentLanguage(lang);
  }

  private updateCurrentLanguage(lang: any) {
    this.currentLanguageName = lang.name;
    this.currentLanguageFlag = lang.flag;
  }

  togglePasswordVisibility(type: string): void {
    if (type === 'login') {
      this.loginPasswordVisible = !this.loginPasswordVisible;
    }
  }

  submitLogin(e: Event): void {
    e.preventDefault();
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const { email, password, rememberMe } = this.loginForm.value;
    const languageCode = localStorage.getItem('app-lang') || 'en-US';

    if (rememberMe) {
      localStorage.setItem('savedEmail', email);
    } else {
      localStorage.removeItem('savedEmail');
    }

    const request = {
      email: this.loginForm.value.email,
      password: this.loginForm.value.password,
      languageCode: languageCode,
    };

    this.authService
      .login(request)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: async (res) => {
          const ctx: LoginContext = {
            email: email,
            password: password,
            component: this,
          };

          const handler = this.loginHandlerFactory.create();
          await handler.handle(res, ctx);

          const isAdmin = res.role?.roleClaims?.some(
            (item: any) => item.claim.name === 'system_admin_claim',
          );

          const orgId = res.userOrganizations?.[0]?.organizationId;
          var currentUser = this.currentUserService.GetSession();

          if (!isAdmin && orgId) {
            this.orgService.getOrganizationById(orgId).subscribe({
              next: (orgRes) => {
                currentUser.isAssessmentActive =
                  orgRes.data?.isAssessmentActive || false;

                localStorage.setItem(
                  'currentUser',
                  JSON.stringify(currentUser),
                );
              },
              error: () => {},
            });
          } else {
            currentUser.isAssessmentActive = false;
            localStorage.setItem('currentUser', JSON.stringify(currentUser));
          }
        },
        error: (err) => {
          console.error('Login failed', err);
        },
      });
  }

  submitFirstLogin(e: Event): void {
    e.preventDefault();
    if (this.firstLoginForm.invalid) {
      this.firstLoginForm.markAllAsTouched();
      return;
    }
    if (
      this.firstLoginForm.value.password !==
      this.firstLoginForm.value.confirmPassword
    ) {
      alert('Passwords do not match!');
      return;
    }
    this.loading = true;
    const formVal = this.firstLoginForm.value;
    const payload = {
      email: this.tempEmail,
      oldPassword: this.tempPassword,
      password: formVal.password,
      confirmPassword: formVal.confirmPassword,
    };
    this.authService
      .firstLogin(payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          if (this.show2FA) {
            this.showFirstLogin = false;
          } else {
            this.router.navigate(['/dashboard']);
          }
        },
        error: (err) => console.error(err),
      });
  }

  onGoogleAuthToggle(): void {
    this.authService.getGoogleAuth().subscribe((res) => {
      console.log('QR Code Data:', res);
    });
  }

  onMailAuthToggle(e: Event): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    if (isChecked) {
      this.authService.sendEmailAuthForEnable().subscribe(() => {
        this.showMailSection = true;
      });
    } else {
      this.showMailSection = false;
    }
  }

  submitMailAuthCode(event: Event) {
    event.preventDefault();

    if (this.mailCodeForm.invalid) {
      this.mailCodeForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const code = this.mailCodeForm.get('code')?.value;

    const request = {
      code: code,
      email: this.loginForm.get('email')?.value,
    };

    this.authService.loginWithMailAuthenticator(request).subscribe({
      next: (res) => {
        this.loading = false;
        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.loading = false;
        console.error('2FA Failed', err);
        this.mailCodeForm.get('code')?.setValue('');
      },
    });
  }

  submitMailLoginForm(e: Event): void {
    e.preventDefault();
    this.router.navigateByUrl('/dashboard');
  }

  submitGoogleAuthCode(event: Event) {
    event.preventDefault();

    if (this.googleCodeForm.invalid) {
      this.googleCodeForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const code = this.googleCodeForm.get('code')?.value;

    const request = {
      code: code,
      email: this.loginForm.get('email')?.value,
    };

    this.authService.loginWithGoogleAuthenticator(request).subscribe({
      next: (res) => {
        this.loading = false;

        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.loading = false;
        console.error('Google 2FA Failed', err);
        this.googleCodeForm.get('code')?.setValue('');
      },
    });
  }

  submitGoogleQrCodeForm(e: Event): void {
    e.preventDefault();
    this.router.navigateByUrl('/dashboard');
  }
}
